import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ComicCard from '@/components/base/ComicCard';
import { comics, genres } from '@/mocks/comics';
import { useSeo } from '@/hooks/useSeo';

const finishedTags = ['全部', '热血', '恋爱', '玄幻', '悬疑', '科幻', '日常', '治愈', '运动'];

export default function Completed() {
  useSeo({
    title: '动漫之家漫画网完结漫画 - 完结漫画大全',
    description:
      '动漫之家漫画网完结漫画大全，汇聚已完结的经典作品，按类型筛选，一次追完不吊胃口。分类清晰，值得一口气看完的好漫画。',
    canonicalPath: '/completed',
  });

  const [tag, setTag] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const list = useMemo(() => {
    let base = comics.filter((c) => c.status === '已完结');
    if (tag !== '全部') base = base.filter((c) => c.genre === tag || c.tags.includes(tag));
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      base = base.filter(
        (c) => c.title.toLowerCase().includes(kw) || c.author.toLowerCase().includes(kw) || c.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return base;
  }, [tag, keyword]);

  return (
    <div>
      {/* Page head */}
      <section className="border-b border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">完结漫画</span>
          </nav>
          <div className="mt-4 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-600">
              <i className="ri-check-double-line"></i> 完结漫画大全
            </span>
            <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">动漫之家完结漫画</h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              已经画上完美句点的经典作品汇聚于此，完结漫画分类清晰、数量齐全，适合一口气追完不吊胃口。
            </p>
            <div className="mt-5 flex max-w-md items-center rounded-full border border-background-300 bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="在完结漫画中搜索…"
                className="w-full bg-transparent px-2 text-sm outline-none placeholder:text-foreground-400"
                aria-label="完结漫画搜索"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filter + grid */}
      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10" aria-label="完结漫画列表">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-sm font-medium text-foreground-700">类型筛选：</span>
          {finishedTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-colors whitespace-nowrap ${
                tag === t ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {t === '全部' ? '全部' : `${t}漫画`}
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-2 text-sm text-foreground-500">
          <i className="ri-file-list-3-line text-primary-500"></i>
          找到 <span className="font-semibold text-foreground-900">{list.length}</span> 部完结漫画
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {list.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
            <i className="ri-emotion-sad-line text-4xl text-background-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到符合条件的完结漫画，换个关键词试试吧。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setTag('全部');
              }}
              className="mt-4 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              清除筛选
            </button>
          </div>
        )}
      </section>

      {/* ItemList JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '动漫之家漫画网完结漫画大全',
            description: '动漫之家漫画网完结漫画大全，汇聚已完结的经典作品，按类型筛选，一次追完。',
            hasPart: list.map((c) => ({
              '@type': 'CreativeWork',
              name: c.title,
              genre: c.genre,
              author: { '@type': 'Person', name: c.author },
            })),
          }),
        }}
      />
    </div>
  );
}